import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faPenRuler, faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import type { ReportTemplateStatus } from '@/services/report-template.service';

/**
 * Lifecycle status for one report template version.
 *
 * Follows ProcedureStatusBadge's visual language. Each status carries an ICON
 * as well as a colour, so the state is never conveyed by colour alone (§17).
 */
const STYLES: Record<
  ReportTemplateStatus,
  { label: string; cls: string; icon: typeof faPenRuler }
> = {
  DRAFT: {
    label: 'Draft',
    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    icon: faPenRuler,
  },
  PUBLISHED: {
    label: 'Published',
    cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    icon: faCircleCheck,
  },
  ARCHIVED: {
    label: 'Archived',
    cls: 'bg-muted text-muted-foreground',
    icon: faBoxArchive,
  },
};

export function ReportTemplateStatusBadge({ status }: { status: ReportTemplateStatus }) {
  const style = STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
        style.cls,
      )}
      data-testid={`template-status-${status}`}
    >
      <FontAwesomeIcon icon={style.icon} className="h-2.5 w-2.5" aria-hidden="true" />
      {style.label}
    </span>
  );
}

/** The tenant's live default. Separate from status: a default is always PUBLISHED. */
export function ReportTemplateDefaultBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary"
      data-testid="template-default-badge"
    >
      <FontAwesomeIcon icon={faCircleCheck} className="h-2.5 w-2.5" aria-hidden="true" />
      Default
    </span>
  );
}

/** The platform-seeded baseline. Read-only in every direction (D-077 #7). */
export function ReportTemplateSystemBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
      data-testid="template-system-badge"
    >
      System
    </span>
  );
}
